import React from 'react'
import { Link } from '@tanstack/react-router'
import { englishRoute, thaiRoute } from 'lib/dictionary-routes'
import { markSearchSource } from 'lib/analytics/events'
import { EngWordFullProps, EngMeaningFullProps } from 'core/interfaces/dictionary-eng-interfaces'
import { WordTranslation, SentenceWithTranslit } from '../../../lib/interfaces'
import { useTranslit } from 'hooks/useTranslit'
import WordWithTooltip from '../../tooltip/word-with-tooltip'
import ExampleSentencesList from '../example-sentences-list'
import ExampleSentencesHeader from '../example-sentences-header'
import PartOfSpeechChips from '../part-of-speech-chips'
import { getTranslationsFromMeaning } from 'lib/getTranslationsFromMeaning'

type Props = {
   engDictionaryWord: EngWordFullProps
   // Token lookups for every Thai phrase on the page, keyed by phrase text.
   translations: WordTranslation[]
   // Word-level fallback: sentences not yet classified to a sense.
   exampleSentences: SentenceWithTranslit[]
   exampleSentencesByMeaningId: Record<number, SentenceWithTranslit[]>
}

// How to phrase each inflection relationship in the note that rides alongside
// the part of speech on a meaning inherited from the base word.
const INFLECTION_PHRASE: Record<string, string> = {
   Plural: 'plural of',
   Comparative: 'comparative of',
   Superlative: 'superlative of',
   'Third Person': 'third person singular of',
   'Present Participle': 'present participle of',
   'Past Participle': 'past participle of',
   Past: 'past tense of',
}
const inflectionPhrase = (inflectionType?: string | null) =>
   (inflectionType && INFLECTION_PHRASE[inflectionType]) || 'inflected form of'

// One Thai translation in the meaning line: the phrase linking to its Thai
// entry, with the per-token hover tooltips the example sentences use, followed
// by its transliteration.
const MeaningPhrase = ({
   phrase,
   translation,
}: {
   phrase: string
   translation?: WordTranslation
}) => {
   const translit = useTranslit()

   const wordObjects = translation?.wordObjects ?? []
   const translitLine = wordObjects
      .map((wo) => translit.of(wo.phonetic))
      .join(' ')
      .trim()

   return (
      <>
         <Link
            {...thaiRoute(phrase)}
            className="text-bodyBlack text-[28px] md:text-[32px] hover:text-[hsla(212,80%,39%,1)] thai"
            onClick={() => markSearchSource('result_click')}
         >
            {wordObjects.length > 0
               ? wordObjects.map((wordObject, index) => (
                    <WordWithTooltip wordObject={wordObject} key={index} />
                 ))
               : phrase}
         </Link>
         {translitLine && (
            <span className="ml-[0.4em] font-translit text-[17px] font-normal italic text-translit whitespace-nowrap">
               {translitLine}
            </span>
         )}
      </>
   )
}

type SenseProps = {
   meaning: EngMeaningFullProps
   index: number
   // The page's headword, picked out where it appears in each example
   // sentence.
   word: string
   // The base word of an inflected form (glasses → glass) and how they relate,
   // for the note on each inherited sense.
   lemma?: string | null
   inflectionType?: string | null
   translations: WordTranslation[]
   // Sentences classified to this sense.
   exampleSentences: SentenceWithTranslit[]
   // Prefix the sense with its "1." index. Suppressed for single-sense words,
   // where a lone "1." reads as noise.
   showNumber: boolean
}

// One sense: its part of speech as a chip, then the sense's Thai translations
// as the big comma-separated meaning line — the same slot and treatment the
// Thai page gives its English meanings (see thai/thai-word-meanings and
// thai-word-meaning-text) — then the sentences that use them.
const Sense = (props: SenseProps) => {
   const { meaning, index, lemma, inflectionType } = props
   const number = props.showNumber ? `${index + 1}.` : null

   // A proper-noun sense reads "Proper Noun" (mirroring the Thai page's
   // getFullPos) rather than the stored base POS "noun".
   const posValue = meaning.isProperNoun ? 'ProperNoun' : meaning.partOfSpeech

   // The note beside the chips: the inflection relationship on a sense
   // inherited from the base word ("plural of glass" — the form's own extra
   // senses, glasses = spectacles, omit it), then the authored summary
   // disambiguating the sense ("a greeting"), then any stored context.
   const inflectionNote = meaning.inherited && lemma && (
      <>
         {inflectionPhrase(inflectionType)}{' '}
         <Link {...englishRoute(lemma)} className="font-bold color-inherit hover:text-translit">
            {lemma}
         </Link>
      </>
   )
   const textNotes = [meaning.summary?.trim(), meaning.explanation?.trim()]
      .filter(Boolean)
      .join(', ')
   const hasNote = Boolean(inflectionNote || textNotes)

   // A sense inherited from the base word carries the base word's sentences
   // ("glass" on the /english/glasses page), so that is the form to pick out.
   const engHeadword = meaning.inherited && lemma ? lemma : props.word

   const phrases = getTranslationsFromMeaning(meaning)
   const hasSentences = props.exampleSentences.length > 0

   return (
      <li
         id={`sense-${index + 1}`}
         className="list-none pt-[6px] pb-[10px] m-0 scroll-mt-[90px] [&~li]:border-t [&~li]:border-t-hairline [&~li]:mt-[32px] [&~li]:pt-[32px]"
      >
         <div className="flex flex-wrap items-center gap-[8px] mb-[8px]">
            <PartOfSpeechChips value={posValue} />
            {hasNote && (
               <span className="text-[13px] italic text-textMuted">
                  {inflectionNote}
                  {inflectionNote && textNotes ? ', ' : ''}
                  {textNotes}
               </span>
            )}
         </div>

         {phrases.length > 0 && (
            <div className="text-[26px] font-extrabold tracking-[-0.01em] text-bodyBlack leading-[1.2]">
               {number && <span className="text-textMuted font-extrabold">{number}</span>}
               {number ? ' ' : ''}
               {phrases.map((phrase, phraseIndex) => (
                  <React.Fragment key={phraseIndex}>
                     {phraseIndex > 0 && <span className="mr-[0.3em]">,</span>}
                     <MeaningPhrase
                        phrase={phrase}
                        translation={props.translations.find((t) => t.unspacedText === phrase)}
                     />
                  </React.Fragment>
               ))}
            </div>
         )}

         {hasSentences && (
            <div className="mt-[40px]">
               <ExampleSentencesHeader />
               <ExampleSentencesList
                  exampleSentences={props.exampleSentences}
                  headword={phrases}
                  engHeadword={engHeadword}
                  embedded
               />
            </div>
         )}
      </li>
   )
}

const EnglishWordThaiMeanings = (props: Props) => {
   const { engDictionaryWord } = props
   const { lemma, inflectionType, meanings } = engDictionaryWord

   // The leftover sentences at the bottom aren't tied to one sense, so any of
   // the word's Thai translations is a fair pick-out there.
   const allPhrases = [...new Set(meanings.flatMap(getTranslationsFromMeaning))]

   return (
      <div>
         <ul id="english-word-thai-meanings" className="mt-[28px] mb-0 mx-0 p-0 list-none">
            {meanings.map((meaning, index) => (
               <Sense
                  key={index}
                  meaning={meaning}
                  index={index}
                  word={engDictionaryWord.word}
                  lemma={lemma}
                  inflectionType={inflectionType}
                  translations={props.translations}
                  exampleSentences={props.exampleSentencesByMeaningId[meaning.id] ?? []}
                  showNumber={meanings.length > 1}
               />
            ))}
         </ul>

         <ExampleSentencesList
            exampleSentences={props.exampleSentences}
            headword={allPhrases}
            engHeadword={engDictionaryWord.word}
            title={
               Object.keys(props.exampleSentencesByMeaningId).length > 0
                  ? 'More real sentences'
                  : undefined
            }
         />
      </div>
   )
}

export default EnglishWordThaiMeanings
